import { useMemo } from 'react'

import {
  AddFieldGroupToRecordTemplateMutation,
  AddFieldGroupToRecordTemplateMutationVariables,
  AddFieldGroupToRecordTypeMutation,
  AddFieldGroupToRecordTypeMutationVariables,
  AddFieldToFieldGroupMutation,
  AddFieldToFieldGroupMutationVariables,
  GetFieldGroupByIdQuery,
  GetFieldGroupByIdQueryVariables,
  GetFieldGroupTemplateByIdQuery,
  GetFieldGroupTemplateByIdQueryVariables,
  PromoteFieldGroupToFieldGroupOnRecordSettingsMutation,
  PromoteFieldGroupToFieldGroupOnRecordSettingsMutationVariables,
  ReorderFieldGroupMutation,
  ReorderFieldGroupMutationVariables,
  UpdateFieldGroupMutation,
  UpdateFieldGroupMutationVariables,
} from 'types/graphql'

import { QueryKey, useMutation, useQuery } from '../utils/queryClient'

const Reorder = gql`
  mutation ReorderFieldGroupMutation($id: Int!, $newOrder: [Int!]!) {
    reorderFieldGroup(id: $id, newOrder: $newOrder) {
      id
    }
  }
`
export const useReorderFieldGroupMutation = () => {
  return useMutation<ReorderFieldGroupMutation, ReorderFieldGroupMutationVariables>({
    mutationDocument: Reorder,
  })
}

const CreateForRecordTemplate = gql`
  mutation AddFieldGroupToRecordTemplateMutation($id: Int!) {
    addFieldGroupToRecordTemplate(id: $id) {
      id
    }
  }
`

export const useAddFieldGroupToRecordTemplateMutation = () => {
  return useMutation<
    AddFieldGroupToRecordTemplateMutation,
    AddFieldGroupToRecordTemplateMutationVariables
  >({ mutationDocument: CreateForRecordTemplate })
}
const CreateForRecordType = gql`
  mutation AddFieldGroupToRecordTypeMutation($id: Int!) {
    addFieldGroupToRecordType(id: $id) {
      id
    }
  }
`
export const useAddFieldGroupToRecordTypeMutation = () => {
  return useMutation<AddFieldGroupToRecordTypeMutation, AddFieldGroupToRecordTypeMutationVariables>(
    { mutationDocument: CreateForRecordType }
  )
}

const AddFieldToFieldGroup = gql`
  mutation AddFieldToFieldGroupMutation($input: AddFieldToFieldGroupInput!) {
    addFieldToFieldGroup(input: $input) {
      id
    }
  }
`
export const useAddFieldToFieldGroupMutation = () => {
  return useMutation<AddFieldToFieldGroupMutation, AddFieldToFieldGroupMutationVariables>({
    mutationDocument: AddFieldToFieldGroup,
  })
}

const Get = gql`
  query GetFieldGroupByIdQuery($id: Int!) {
    fieldGroupById(id: $id) {
      ...FieldGroupFragment
    }
  }
`
export const useFieldGroupByIdQuery = (id: number) => {
  const { data, ...rest } = useQuery<GetFieldGroupByIdQuery, GetFieldGroupByIdQueryVariables>({
    queryKey: [QueryKey.FieldGroup, id],
    queryDocument: Get,
    variables: {
      id,
    },
  })

  const fields = useMemo(() => {
    return data?.fieldGroupById?.fields ?? []
  }, [data])

  const fieldGroupById = useMemo(
    () => ({
      ...data?.fieldGroupById,
      fields,
    }),
    [data?.fieldGroupById, fields]
  )

  return {
    fieldGroupById,
    ...rest,
  }
}

const GetTemplate = gql`
  query GetFieldGroupTemplateByIdQuery($id: Int!) {
    fieldGroupById(id: $id) {
      ...FieldGroupTemplateDetailedFragment
    }
  }
`

export const useFieldGroupTemplateByIdQuery = (id: number) => {
  const { data, ...rest } = useQuery<
    GetFieldGroupTemplateByIdQuery,
    GetFieldGroupTemplateByIdQueryVariables
  >({
    queryKey: [QueryKey.FieldGroupTemplate, id],
    queryDocument: GetTemplate,
    variables: { id },
  })

  const applicationFields = useMemo(() => {
    return data?.fieldGroupById?.applicationFields ?? []
  }, [data])

  const fields = useMemo(() => {
    return data?.fieldGroupById?.fields ?? []
  }, [data])

  return {
    fieldGroupTemplate: {
      ...data?.fieldGroupById,
      applicationFields,
      fields,
    },
    ...rest,
  }
}

const Promote = gql`
  mutation PromoteFieldGroupToFieldGroupOnRecordSettingsMutation(
    $input: PromoteFieldGroupToFieldGroupOnRecordSettingsInput!
  ) {
    promoteFieldGroupToFieldGroupOnRecordSettings(input: $input) {
      id
    }
  }
`
export const usePromoteFieldGroupToFieldGroupOnRecordSettingsMutation = () => {
  return useMutation<
    PromoteFieldGroupToFieldGroupOnRecordSettingsMutation,
    PromoteFieldGroupToFieldGroupOnRecordSettingsMutationVariables
  >({
    mutationDocument: Promote,
  })
}

const Update = gql`
  mutation UpdateFieldGroupMutation($input: UpdateFieldGroupInput!) {
    updateFieldGroup(input: $input) {
      id
    }
  }
`

export const useUpdateFieldGroupMutation = () => {
  return useMutation<UpdateFieldGroupMutation, UpdateFieldGroupMutationVariables>({
    mutationDocument: Update,
  })
}
